import { useEffect, useMemo, useRef, useState } from 'react';

import {
  approveIdea,
  type ContentKind,
  contentPath,
  declineIdea,
  describeFormatPreset,
  fetchContentList,
  fetchIdeaBatch,
  fetchPresets,
  fetchSettings,
  type FormatPreset,
  type Idea,
  type IdeaBatch,
  ideaBatchPath,
  type IdeaUpdate,
  type Settings,
  type ToneOption,
  updateIdea,
  updateIdeaBriefDetails,
} from '../api.ts';
import { IdeaReviewIntro } from '../ApprovalGuide.tsx';
import { BriefDetailsPanel } from '../BriefDetailsFields.tsx';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { describeError, redirectIfUnauthorised, useKeyChords } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { IdeaSection, isSection, seedLine } from '../IdeaSection.tsx';
import { ideaBatchIdFromPath, Link, replaceSearch, useLocation } from '../router.tsx';
import { useToast } from '../toast.tsx';
import { Notice, PageHeader, Segmented, Skeleton } from '../ui.tsx';
import * as wording from '../wording.ts';

/**
 * The one-by-one review (US-124): a focus view that puts one drafted idea in
 * front of the operator with every field editable, and moves on after each
 * decision.
 *
 * The ideas reviewed here are the ones waiting for a decision when the page
 * opened, in batch order. A skipped
 * idea stays drafted, so walking forward past the last one wraps round to it
 * again; when none of them is drafted any more, a summary takes the card's
 * place. `?idea=<id>` keeps the place across a reload.
 */

const TOGGLES = ['captions', 'music', 'voiceover', 'watermark'] as const;
type ToggleField = (typeof TOGGLES)[number];

const TOGGLE_DEFAULT: Record<ToggleField, keyof Settings> = {
  captions: 'defaultCaptions',
  music: 'defaultMusic',
  voiceover: 'defaultVoiceover',
  watermark: 'defaultWatermark',
};

/** The form's own copy of a draft: text where the operator types, the rest as the server has it. */
interface Draft {
  readonly title: string;
  readonly kind: ContentKind;
  readonly formats: readonly string[];
  readonly tone: string;
  readonly brief: string;
  readonly creativeDirection: string;
  readonly targetSeconds: string;
  readonly captions: boolean | null;
  readonly music: boolean | null;
  readonly voiceover: boolean | null;
  readonly watermark: boolean | null;
}

function draftOf(idea: Idea): Draft {
  return {
    title: idea.title ?? '',
    kind: idea.kind ?? 'video',
    formats: idea.formats ?? [],
    tone: idea.tone ?? '',
    brief: idea.brief ?? '',
    creativeDirection: idea.creativeDirection ?? '',
    targetSeconds: idea.targetSeconds === null ? '' : String(idea.targetSeconds),
    captions: idea.captions,
    music: idea.music,
    voiceover: idea.voiceover,
    watermark: idea.watermark,
  };
}

/** Only the fields the operator changed, in the shape `PATCH` takes them. */
function changesOf(saved: Draft, draft: Draft): IdeaUpdate {
  const update: IdeaUpdate = {};
  if (draft.title !== saved.title) update.title = draft.title.trim();
  if (draft.kind !== saved.kind) update.kind = draft.kind;
  if (draft.formats.join('\n') !== saved.formats.join('\n')) update.formats = [...draft.formats];
  if (draft.tone !== saved.tone) update.tone = draft.tone;
  if (draft.brief !== saved.brief) update.brief = draft.brief.trim();
  if (draft.creativeDirection !== saved.creativeDirection) {
    const direction = draft.creativeDirection.trim();
    update.creativeDirection = direction === '' ? null : direction;
  }
  if (draft.targetSeconds !== saved.targetSeconds) {
    const seconds = draft.targetSeconds.trim();
    update.targetSeconds = seconds === '' ? null : Number(seconds);
  }
  for (const field of TOGGLES) {
    if (draft[field] !== saved[field]) update[field] = draft[field];
  }
  return update;
}

/** The next drafted idea after `from` in batch order, wrapping round; null when none is left. */
function nextDrafted(ideas: readonly Idea[], from: number): Idea | null {
  const drafted = ideas.filter((idea) => idea.status === 'drafted');
  return drafted.find((idea) => idea.position > from) ?? drafted[0] ?? null;
}

/** The closest drafted idea before `from`; null at the start. */
function previousDrafted(ideas: readonly Idea[], from: number): Idea | null {
  return ideas.filter((idea) => idea.status === 'drafted' && idea.position < from).at(-1) ?? null;
}

export function IdeaReview() {
  const { pathname, search } = useLocation();
  const batchId = ideaBatchIdFromPath(pathname) ?? '';
  const wanted = new URLSearchParams(search).get('idea');
  const toast = useToast();

  const [batch, setBatch] = useState<IdeaBatch | null>(null);
  /** The ideas this review covers, by id: drafted when first seen. */
  const [session, setSession] = useState<ReadonlySet<string>>(new Set());
  const [presets, setPresets] = useState<FormatPreset[] | null>(null);
  const [tones, setTones] = useState<ToneOption[] | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const fail = (cause: unknown): void => {
      if (controller.signal.aborted || redirectIfUnauthorised(cause)) return;
      setError(describeError(cause));
    };
    fetchIdeaBatch(batchId, controller.signal)
      .then((loaded) => {
        setBatch(loaded);
        setSession(new Set(loaded.ideas.filter((idea) => idea.status === 'drafted').map((idea) => idea.id)));
      })
      .catch(fail);
    fetchPresets(controller.signal).then(setPresets).catch(fail);
    fetchContentList(controller.signal)
      .then((list) => setTones(list.tones))
      .catch(fail);
    // The defaults only say what an idea nobody chose a track for will get.
    fetchSettings(controller.signal)
      .then(setSettings)
      .catch(() => undefined);
    return () => controller.abort();
  }, [batchId]);

  const ideas = useMemo(
    () => (batch === null ? [] : batch.ideas.filter((idea) => session.has(idea.id))),
    [batch, session],
  );

  // The idea on screen: the one in the URL while it still waits, else the first that does.
  const asked = ideas.find((idea) => idea.id === wanted && idea.status === 'drafted') ?? null;
  const current = asked ?? nextDrafted(ideas, -1);

  useEffect(() => {
    if (batch === null) return;
    const id = current?.id ?? null;
    if (id === wanted) return;
    const next = new URLSearchParams(window.location.search);
    if (id === null) next.delete('idea');
    else next.set('idea', id);
    replaceSearch(next);
  }, [batch, current, wanted]);

  const go = (idea: Idea | null): void => {
    const next = new URLSearchParams(window.location.search);
    if (idea === null) next.delete('idea');
    else next.set('idea', idea.id);
    replaceSearch(next);
  };

  /** Puts the server's answer on the idea, then moves to the next one still waiting. */
  const decided = (idea: Idea): void => {
    if (batch === null) return;
    const updated = batch.ideas.map((each) => (each.id === idea.id ? idea : each));
    setBatch({ ...batch, ideas: updated });
    go(nextDrafted(updated.filter((each) => session.has(each.id)), idea.position));
  };

  const saved = (idea: Idea): void => {
    setBatch((currentBatch) =>
      currentBatch === null
        ? currentBatch
        : { ...currentBatch, ideas: currentBatch.ideas.map((each) => (each.id === idea.id ? idea : each)) },
    );
  };

  const back = { href: ideaBatchPath(batchId), label: batch?.title ?? wording.IDEA_REVIEW_BACK };
  const loading = batch === null || presets === null || tones === null;

  if (loading) {
    return (
      <div className="page page--narrow">
        <PageHeader title={wording.IDEA_REVIEW_TITLE} back={back} />
        {error !== null ? (
          <Notice kind="error">
            {wording.BATCH_READ_FAILED} {error}
          </Notice>
        ) : (
          <div className="panel">
            <div className="panel__body">
              <Skeleton lines={8} />
            </div>
          </div>
        )}
      </div>
    );
  }

  const index = current === null ? -1 : ideas.indexOf(current);

  return (
    <div className="page page--narrow">
      <PageHeader
        title={wording.IDEA_REVIEW_TITLE}
        back={back}
        subtitle={current === null ? undefined : wording.ideaOfToDecide(index + 1, ideas.length)}
      />
      {current === null ? (
        <Summary batchId={batch.id} ideas={ideas} />
      ) : (
        <>
          <IdeaReviewIntro />
          {/* The marketing side of the idea, carried to the content on approval (US-007);
              above the card, so it is read before the decision. */}
          <BriefDetailsPanel
            key={`brief-${current.id}`}
            id="idea-brief-details"
            details={current.briefDetails}
            onSave={async (details) => {
              saved(await updateIdeaBriefDetails(batch.id, current.id, details));
              toast.ok('Brief details saved.');
            }}
          />
          <IdeaCard
            key={current.id}
            batchId={batch.id}
            idea={current}
            presets={presets}
            tones={tones}
            settings={settings}
            onDecided={decided}
            onSaved={saved}
            onSkip={() => go(nextDrafted(ideas, current.position))}
            onBack={() => {
              const previous = previousDrafted(ideas, current.position);
              if (previous !== null) go(previous);
            }}
            hasPrevious={previousDrafted(ideas, current.position) !== null}
            hasNext={nextDrafted(ideas, current.position)?.id !== current.id}
          />
        </>
      )}
    </div>
  );
}

/** Once nothing is left to decide: what this review came to. */
function Summary({ batchId, ideas }: { readonly batchId: string; readonly ideas: readonly Idea[] }) {
  const approved = ideas.filter((idea) => idea.status === 'approved').length;
  const declined = ideas.filter((idea) => idea.status === 'declined').length;
  return (
    <div className="panel">
      <div className="panel__body review__summary">
        <Icon name="check-circle" />
        <p>{approved + declined === 0 ? wording.NOTHING_TO_REVIEW : wording.ideasReviewed(approved, declined)}</p>
        <Link className="button button--primary" href={ideaBatchPath(batchId)}>
          {wording.REVIEW_BACK_TO_BATCH}
        </Link>
      </div>
    </div>
  );
}

function IdeaCard({
  batchId,
  idea,
  presets,
  tones,
  settings,
  onDecided,
  onSaved,
  onSkip,
  onBack,
  hasPrevious,
  hasNext,
}: {
  readonly batchId: string;
  readonly idea: Idea;
  readonly presets: readonly FormatPreset[];
  readonly tones: readonly ToneOption[];
  readonly settings: Settings | null;
  readonly onDecided: (idea: Idea) => void;
  readonly onSaved: (idea: Idea) => void;
  readonly onSkip: () => void;
  readonly onBack: () => void;
  readonly hasPrevious: boolean;
  readonly hasNext: boolean;
}) {
  const toast = useToast();
  const titleRef = useRef<HTMLInputElement>(null);
  const [saved, setSaved] = useState<Draft>(() => draftOf(idea));
  const [draft, setDraft] = useState<Draft>(saved);
  const [busy, setBusy] = useState<'saving' | 'approving' | 'declining' | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  /** A move waiting on "Discard your changes?". */
  const [leaving, setLeaving] = useState<(() => void) | null>(null);

  const changes = changesOf(saved, draft);
  const dirty = Object.keys(changes).length > 0;
  const dialogOpen = declining || leaving !== null;
  const invalid =
    draft.title.trim() === '' ||
    draft.formats.length === 0 ||
    (draft.kind === 'video' &&
      draft.targetSeconds.trim() !== '' &&
      !Number.isInteger(Number(draft.targetSeconds)));
  const id = (field: string): string => `idea-${field}`;

  const set = <K extends keyof Draft>(field: K, value: Draft[K]): void => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const offered = presets.filter((preset) => preset.kinds.includes(draft.kind));
  const chooseKind = (kind: ContentKind): void => {
    setDraft((current) => ({
      ...current,
      kind,
      // Keep only the formats the new kind can be made in, as the new-content form does.
      formats: current.formats.filter((format) =>
        presets.some((preset) => preset.preset === format && preset.kinds.includes(kind)),
      ),
    }));
  };
  const toggleFormat = (format: string): void => {
    setDraft((current) => ({
      ...current,
      formats: current.formats.includes(format)
        ? current.formats.filter((each) => each !== format)
        : [...current.formats, format],
    }));
  };

  const refuse = (prefix: string, cause: unknown): void => {
    if (redirectIfUnauthorised(cause)) return;
    setRefusal(`${prefix} ${describeError(cause)}`);
  };

  const approve = async (): Promise<void> => {
    if (busy !== null || invalid) return;
    setRefusal(null);
    if (dirty) {
      setBusy('saving');
      try {
        const updated = await updateIdea(batchId, idea.id, changes);
        const next = draftOf(updated);
        setSaved(next);
        setDraft(next);
        onSaved(updated);
      } catch (cause) {
        setBusy(null);
        refuse(wording.IDEA_SAVE_FAILED, cause);
        return;
      }
    }
    setBusy('approving');
    try {
      const approval = await approveIdea(batchId, idea.id);
      toast.ok(wording.contentBeingPlanned(approval.idea.content?.name ?? approval.contentId), {
        href: contentPath(approval.contentId),
      });
      onDecided(approval.idea);
    } catch (cause) {
      setBusy(null);
      refuse(wording.APPROVE_FAILED, cause);
    }
  };

  const decline = (): void => {
    const trimmed = reason.trim();
    setBusy('declining');
    setRefusal(null);
    declineIdea(batchId, idea.id, trimmed === '' ? null : trimmed)
      .then((declined) => {
        setDeclining(false);
        onDecided(declined);
      })
      .catch((cause: unknown) => {
        setBusy(null);
        setDeclining(false);
        refuse(wording.DECLINE_FAILED, cause);
      });
  };

  /** Skip and back leave the idea as it was saved; changes are only dropped once confirmed. */
  const leave = (move: () => void): void => {
    if (busy !== null) return;
    if (dirty) setLeaving(() => move);
    else move();
  };

  useKeyChords({
    a: () => {
      if (!dialogOpen) void approve();
    },
    d: () => {
      if (!dialogOpen && busy === null) setDeclining(true);
    },
    j: () => {
      if (!dialogOpen && hasNext) leave(onSkip);
    },
    arrowright: () => {
      if (!dialogOpen && hasNext) leave(onSkip);
    },
    k: () => {
      if (!dialogOpen && hasPrevious) leave(onBack);
    },
    arrowleft: () => {
      if (!dialogOpen && hasPrevious) leave(onBack);
    },
    e: () => {
      if (!dialogOpen) titleRef.current?.focus();
    },
  });

  return (
    <div className="panel review">
      <div className="panel__body review__body">
        <div className="review__seed">
          <span className="field__label">
            {idea.source === 'agent' ? wording.IDEA_SUGGESTED_SEED : wording.IDEA_SEED}
          </span>
          {isSection(idea) ? (
            <>
              <p>{seedLine(idea)}</p>
              <IdeaSection seed={idea.seed} />
            </>
          ) : (
            <p>{idea.seed}</p>
          )}
          {idea.rationale !== null && idea.rationale !== '' && (
            <>
              <span className="field__label">{wording.IDEA_RATIONALE}</span>
              <p className="muted">{idea.rationale}</p>
            </>
          )}
        </div>

        {refusal !== null && <Notice kind="error">{refusal}</Notice>}

        <div className="field">
          <label className="field__label" htmlFor={id('title')}>
            {wording.IDEA_TITLE}
          </label>
          <input
            id={id('title')}
            ref={titleRef}
            className="field__input"
            value={draft.title}
            onChange={(event) => set('title', event.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="field">
          <span className="field__label">{wording.IDEA_KIND_LABEL}</span>
          <div>
            <Segmented
              value={draft.kind}
              ariaLabel={wording.IDEA_KIND_LABEL}
              options={(['video', 'image'] as const).map((kind) => ({ value: kind, label: wording.IDEA_KIND[kind] }))}
              onChange={chooseKind}
            />
          </div>
        </div>

        <div className="field">
          <span className="field__label">{wording.IDEA_FORMATS}</span>
          <ul className="choices">
            {offered.map((preset) => (
              <li className="choice" key={preset.preset}>
                <label className="choice__label">
                  <input
                    type="checkbox"
                    checked={draft.formats.includes(preset.preset)}
                    onChange={() => toggleFormat(preset.preset)}
                  />
                  <span className="choice__main">
                    <span className="choice__title">{preset.preset}</span>
                    <span className="choice__summary mono">{describeFormatPreset(preset)}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className={draft.formats.length === 0 ? 'field__hint field__hint--error' : 'field__hint'}>
            {draft.formats.length === 0 ? wording.IDEA_NO_FORMATS : wording.IDEA_FORMATS_HINT}
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={id('tone')}>
            {wording.IDEA_TONE}
          </label>
          <select
            id={id('tone')}
            className="field__input field__input--narrow"
            value={draft.tone}
            onChange={(event) => set('tone', event.target.value)}
          >
            {!tones.some((option) => option.tone === draft.tone) && <option value={draft.tone}>{draft.tone}</option>}
            {tones.map((option) => (
              <option key={option.tone} value={option.tone}>
                {option.label ?? option.tone}
              </option>
            ))}
          </select>
          <p className="field__hint">{tones.find((option) => option.tone === draft.tone)?.summary}</p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={id('brief')}>
            {wording.IDEA_BRIEF}
          </label>
          <textarea
            id={id('brief')}
            className="field__input field__textarea"
            rows={6}
            value={draft.brief}
            onChange={(event) => set('brief', event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor={id('direction')}>
            {wording.IDEA_DIRECTION} <span className="muted">— {wording.OPTIONAL}</span>
          </label>
          <input
            id={id('direction')}
            className="field__input"
            value={draft.creativeDirection}
            onChange={(event) => set('creativeDirection', event.target.value)}
            autoComplete="off"
          />
        </div>

        {draft.kind === 'video' && (
          <>
            <div className="field">
              <label className="field__label" htmlFor={id('seconds')}>
                {wording.IDEA_LENGTH}
              </label>
              <input
                id={id('seconds')}
                className="field__input field__input--narrow"
                type="number"
                min={5}
                max={90}
                step={1}
                value={draft.targetSeconds}
                onChange={(event) => set('targetSeconds', event.target.value)}
                aria-describedby={id('seconds-hint')}
              />
              <p className="field__hint" id={id('seconds-hint')}>
                {wording.IDEA_LENGTH_HINT}
              </p>
            </div>

            <div className="field">
              <span className="field__label">{wording.IDEA_TRACKS}</span>
              <ul className="choices">
                {TOGGLES.map((field) => {
                  const fallback = settings === null ? false : settings[TOGGLE_DEFAULT[field]] === true;
                  return (
                    <li className="choice" key={field}>
                      <label className="choice__label">
                        <input
                          type="checkbox"
                          checked={draft[field] ?? fallback}
                          onChange={(event) => set(field, event.target.checked)}
                        />
                        <span className="choice__main">
                          <span className="choice__title">{wording.IDEA_TOGGLES[field]}</span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              <p className="field__hint">{wording.IDEA_TRACKS_HINT}</p>
            </div>
          </>
        )}

        <div className="review__actions">
          <button
            type="button"
            className="button button--quiet"
            disabled={busy !== null || !hasPrevious}
            onClick={() => leave(onBack)}
          >
            <Icon name="arrow-left" />
            {wording.PREVIOUS}
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={busy !== null || !hasNext}
            onClick={() => leave(onSkip)}
          >
            {wording.SKIP}
            <Icon name="chevron-right" />
          </button>
          <span className="review__spacer" />
          <button type="button" className="button" disabled={busy !== null} onClick={() => setDeclining(true)}>
            {busy === 'declining' ? wording.WORKING : wording.DECLINE}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={busy !== null || invalid}
            onClick={() => void approve()}
          >
            <Icon name="check" />
            {busy === 'saving' ? wording.SAVING : busy === 'approving' ? wording.APPROVING : wording.APPROVE}
          </button>
        </div>
        <p className="field__hint review__keys">{wording.KEYBOARD_HINT}</p>
      </div>

      <ConfirmDialog
        open={declining}
        title={wording.DECLINE_TITLE}
        confirmLabel={wording.DECLINE_YES}
        cancelLabel={wording.CANCEL}
        busy={busy === 'declining'}
        onConfirm={decline}
        onCancel={() => {
          setDeclining(false);
          setReason('');
        }}
      >
        <p>
          <strong>{draft.title === '' ? seedLine(idea) : draft.title}</strong>
        </p>
        <label className="field__label" htmlFor={id('decline-reason')}>
          {wording.DECLINE_REASON}
        </label>
        <textarea
          id={id('decline-reason')}
          className="field__input"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <p className="field__hint">{wording.DECLINE_REASON_HINT}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={leaving !== null}
        title={wording.DISCARD_TITLE}
        confirmLabel={wording.DISCARD_YES}
        cancelLabel={wording.KEEP_EDITING}
        danger
        onConfirm={() => {
          const move = leaving;
          setLeaving(null);
          move?.();
        }}
        onCancel={() => setLeaving(null)}
      >
        <p>{wording.DISCARD_BODY}</p>
      </ConfirmDialog>
    </div>
  );
}
